import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useWhiteLabel } from '../WhiteLabelProvider';
import {
  createDynamicThemes,
  applyCssCustomProperties,
  getCurrentThemeMode,
} from '../dynamicThemeGenerator';
import { selectLayout } from '@src/store/layout/layoutSlice';

/**
 * Enhanced hook that applies white label configuration and integrates with theme system
 */
export const useEnhancedWhiteLabel = () => {
  const whiteLabel = useWhiteLabel();
  const layout = useSelector(selectLayout);

  useEffect(() => {
    if (!whiteLabel) return;

    // Update document title
    document.title = whiteLabel.brandName;

    // Update favicon
    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = whiteLabel.faviconUrl;

    // Apply dynamic themes based on white label config and current theme mode
    const themes = createDynamicThemes(whiteLabel);
    const currentMode = getCurrentThemeMode();
    const currentTheme = themes[currentMode];

    // Apply CSS custom properties
    applyCssCustomProperties(whiteLabel, currentTheme);

    // Legacy CSS properties for backward compatibility
    const root = document.documentElement;
    if (whiteLabel.primaryColor) {
      root.style.setProperty('--color-primary', whiteLabel.primaryColor);
    }
    if (whiteLabel.secondaryColor) {
      root.style.setProperty('--color-secondary', whiteLabel.secondaryColor);
    }
  }, [whiteLabel, layout.theme]);

  return { whiteLabel, currentTheme: getCurrentThemeMode() };
};
